/**
 * cardAccessService.js — Vergi numarası + fuara özel firma evrakı + kart erişim durumu.
 *
 * Backend #40 (2026-09-16). Kasıtlı olarak AYRI bir dosya: firmaProfileService.js'nin
 * `belgeler`/profile-page gömülü alanı SABİT üç İSG belgesini taşıyor (Taahhütname/Hekim/Uzman);
 * buradaki uçlar TAMAMEN FARKLI bir kavram — fuar başına GENİŞLEYEBİLİR bir belge listesi
 * (fuar_firma_belge_kural) ve buna bağlı kart erişim modu. İkisini aynı dosyada karıştırmak
 * firmaProfileService.js'nin kendi başlık yorumunun uyardığı hatayı tekrarlardı ("ayrı varlık,
 * ayrı dosya").
 *
 * Mock yok — bu uçlar 2026-09-16'da yeni yazıldı, henüz mock fixture'ı olmayan tek servis.
 * Her çağrı canlı backend'e gider (forceLive parametresi de yok, her zaman canlı).
 *
 * Base: {JAVA_BACKEND_API_URL}/firma, {JAVA_BACKEND_API_URL}/fuar, {JAVA_BACKEND_API_URL}/personnel
 * Auth: getAuthHeader()
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const FIRMA_API = `${JAVA_BACKEND_API_URL}/firma`;
const FUAR_API = `${JAVA_BACKEND_API_URL}/fuar`;
const PERSONNEL_API = `${JAVA_BACKEND_API_URL}/personnel`;

/** cardAccessMode sözlüğü — backend CardAccessModeConstant ile birebir. */
export const CARD_ACCESS_MODE = {
  BLOCKED_NO_TAX_NUMBER: {
    color: 'danger',
    get label() { return i18n.t("services:cardAccessService.vergiNumarasiGirilmedi"); },
    get hint() { return i18n.t("services:cardAccessService.buFirmaIcinPersonelIslemleri"); },
  },
  TEMPORARY_24H: {
    color: 'warning',
    get label() { return i18n.t("services:cardAccessService.geciciErisim24Saat"); },
    // WAS (until #334): hint: 'Vergi numarası girildi, ama fuarın istediği firma evrakları tamamlanmadı. '
    //   + 'Kart basılabilir; basımdan itibaren yalnızca 24 saat geçerlidir.',
    get hint() { return i18n.t("services:cardAccessService.gecici24SaatIpucu"); },
  },
  FULL: {
    color: 'success',
    get label() { return i18n.t("services:cardAccessService.tamErisim"); },
    get hint() { return i18n.t("services:cardAccessService.vergiNumarasiVeFuarinIstedigi"); },
  },
};

/** GET /firma/{firmaId}/tax-number → { firmaId, taxNumber } — taxNumber null olabilir. */
export async function getTaxNumber(firmaId) {
  const response = await axios.get(`${FIRMA_API}/${firmaId}/tax-number`, { headers: getAuthHeader() });
  return response.data?.taxNumber ?? null;
}

/**
 * #435: GET /firma/{firmaId}/tax-number → { taxNumber, gerekli }. gerekli false = a Yabancı company owes no tax number
 * (backend #434, taxNumberGerekli); a backend that does not send the flag counts as "owed", the old behaviour.
 */
export async function getVergiNoDurumu(firmaId) {
  const response = await axios.get(`${FIRMA_API}/${firmaId}/tax-number`, { headers: getAuthHeader() });
  return { taxNumber: response.data?.taxNumber ?? null, gerekli: response.data?.taxNumberGerekli !== false };
}

/**
 * PUT /firma/{firmaId}/tax-number { taxNumber }. Boş/blank değer temizler (tekrar "girilmedi").
 * "İşlem yapılamaz" mesajını GenericResultJson.message'dan üretiyoruz — 400'de de body geliyor.
 */
export async function setTaxNumber(firmaId, taxNumber) {
  const response = await axios.put(
    `${FIRMA_API}/${firmaId}/tax-number`,
    { taxNumber: taxNumber ?? '' },
    { headers: getAuthHeader() }
  );
  return response.data;
}

/**
 * GET /firma/{firmaId}/kart-erisim?fuarId= — firma seviyesi durum:
 * { firmaId, fuarId, taxNumberSet, taxNumberGerekli (#434: false = Yabancı), requiredFileTypeIds, missingFileTypeIds, fuarBelgeleriTamam,
 *   cardAccessMode }
 */
export async function getFirmaCardAccessStatus(firmaId, fuarId) {
  const response = await axios.get(`${PERSONNEL_API}/firma/${firmaId}/kart-erisim`, {
    params: { fuarId },
    headers: getAuthHeader(),
  });
  return response.data;
}

/**
 * GET /personnel/{personId}/erisim-gecerlilik?firmaId=&fuarId= — kişi seviyesi:
 * { personId, cardEligible, cardAccessMode, accessValidUntil, expired }
 * `accessValidUntil` ISO tarih/saat string'i ya da null (henüz basılmamış / erişim yok).
 */
export async function getPersonAccessWindow(personId, firmaId, fuarId) {
  const response = await axios.get(`${PERSONNEL_API}/${personId}/erisim-gecerlilik`, {
    params: { firmaId, fuarId },
    headers: getAuthHeader(),
  });
  return response.data;
}

/**
 * POST /personnel/{personId}/kart-yazdir?firmaId=&fuarId= — kartı "bastı" olarak işaretler
 * (card_counter.printed_at = NOW()), TEMPORARY_24H modunun 24 saatlik saatini bu an başlatır.
 * BLOCKED_NO_TAX_NUMBER modunda reddedilir (result:false) — TEMPORARY_24H'de ise asıl amacı bu.
 *
 * Cevap `{ message, result, data: { personId, cardEligible, cardAccessMode, accessValidUntil, expired } }`;
 * `accessValidUntil` TEMPORARY_24H'te yerel tarih-saat ("2026-10-02T14:30:00"), FULL'de "yyyy-MM-dd".
 * ⚠️ İdempotent değil (her çağrı sayacı artırır, `printed_at`i sıfırlar) ve uygunluğa BAKMAZ —
 * yasaklı/uygunsuz kişi de 200 alır (kapı: backlog #127). Staff her firma için çağırabilir.
 * İlk arayüzü Kişi Listesi'ndeki Kart Yazdır (Components/Kisi/KartYazdirModal.jsx, #128).
 *
 * #467 (backend #396): `meslekId` kartta ne yazacağını söyler — verilmezse (undefined/null) backend seçer (Çalışan basılabiliyorsa Çalışan,
 * yoksa basılabilir ilk meslek), `0` kesin Çalışan, başka bir değer kişinin tuttuğu bir meslek. Seçilen basılamıyorsa 400 (TR/EN `message`),
 * sayaç oynamadan. `data.kartBasim` = kartta yazan: { firmaName, meslekId (null = Çalışan), meslekAdi, kategoriAdi, otomatik, sayac, basimTarihi, … }.
 * Arayüz her zaman açık bir değer gönderir (Çalışan için 0): ekranda seçilen ile basılan aynı olsun.
 */
// WAS (until #467): export async function printCard(personId, firmaId, fuarId) { … params: { firmaId, fuarId } … }
export async function printCard(personId, firmaId, fuarId, meslekId = null) {
  const params = { firmaId, fuarId };
  if (meslekId !== null && meslekId !== undefined) params.meslekId = meslekId;
  const response = await axios.post(
    `${PERSONNEL_API}/${personId}/kart-yazdir`,
    null,
    { params, headers: getAuthHeader() }
  );
  return response.data;
}

/**
 * GET /personnel/{personId}/kart-basimlari?fuarId=&firmaId= — kişinin kart basımları, en yeni önce (backend #396, `kart_basim`):
 * [{ id, fuarId, fuarName, firmaId, firmaName, firmaShortname, meslekId (null = Çalışan), meslekAdi, kategoriAdi, otomatik,
 *    isverenFirmaName (yalnızca staff), sayac, basanUserName, basimTarihi }]. DÜZ DİZİ (GenericResultJson değil). Staff hepsini görür;
 * firma girişi `firmaId` vermek zorunda ve yalnızca kendi firması altındaki basımları görür. #396 öncesi basımlar yalnızca
 * `card_counter`dadır, burada yoktur.
 */
export async function getKartBasimlari(personId, fuarId, firmaId) {
  const params = {};
  if (fuarId) params.fuarId = fuarId;
  if (firmaId) params.firmaId = firmaId;
  const response = await axios.get(`${PERSONNEL_API}/${personId}/kart-basimlari`, { params, headers: getAuthHeader() });
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * GET /personnel/{personId}/kart-karari?fuarId= — Kart Yazdır'dan ÖNCE bir kişinin bir fuardaki kart kararı
 * (backend #277, yalnızca staff). `fuarId` her zaman ÜST ÇUBUKTAKİ fuardır; sayfa içi ayrı bir fuar değil.
 * { personId, fuarId, name, surname, forbidden, fuardaGorevli, kartFirmaId,
 *   firmalar: [{ firmaId, firmaName, firmaShortname, kartBasilabilir, cardEligible, kartDurumu, kartYolu,
 *                uygunsuzluklar[], kartErisim{ taxNumberSet, cardAccessMode, … }, eksikFuarBelgeleri[],
 *                kisi{ Personel Detayı'nın nesnesi: uyarilar[], documents{}, … } }] }
 * Fuarda görevli değilse `fuardaGorevli: false` ve `firmalar: []`. `kartBasilabilir` tam olarak
 * POST /kart-yazdir'ın kabul ettiği şart (yeşil karar + vergi numarası).
 */
export async function getKartKarari(personId, fuarId) {
  const response = await axios.get(`${PERSONNEL_API}/${personId}/kart-karari`, {
    params: { fuarId },
    headers: getAuthHeader(),
  });
  return response.data;
}

/**
 * GET /fuar/{fuarId}/firma-belge-kurallari → bir DÜZEYİN kuralları: [{ id, fuarId, fileTypeId, fileTypeName, uyruk, durum, updateDate, updatedBy }].
 * #430: fuarId 0 = tüm fuarlar (genel düzey); uyruk 0 hepsi / 1 yerli firma / 2 yabancı firma; durum 1 zorunlu / 2 isteğe bağlı / 3 uygulanamaz / 4 sunulmaz.
 * WAS (#430 öncesi): [{ fileTypeId, fileTypeName }] — fuarın "zorunlu" belge listesi.
 */
export async function getRequiredFirmaBelgeTypes(fuarId) {
  const response = await axios.get(`${FUAR_API}/${fuarId}/firma-belge-kurallari`, { headers: getAuthHeader() });
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * POST /fuar/{fuarId}/firma-belge-kurallari { fileTypeId, uyruk, durum } — staff/admin, Fuar Kuralları / Global Kurallar › Firma Belgeleri.
 * #430: kuralı ekler YA DA durumunu değiştirir (silinmiş satır geri gelir); uyruk 0 hepsi / 1 yerli / 2 yabancı firma (varsayılan 0), durum varsayılan 1 (zorunlu).
 */
export async function addRequiredFirmaBelgeType(fuarId, fileTypeId, { uyruk = 0, durum = 1 } = {}) {
  const response = await axios.post(
    `${FUAR_API}/${fuarId}/firma-belge-kurallari`,
    { fileTypeId, uyruk, durum },
    { headers: getAuthHeader() }
  );
  return response.data;
}

/** DELETE /fuar/{fuarId}/firma-belge-kurallari/{fileTypeId}?uyruk= — #430: hangi uyruk düzeyinin satırı (varsayılan 0). */
export async function removeRequiredFirmaBelgeType(fuarId, fileTypeId, uyruk = 0) {
  const response = await axios.delete(
    `${FUAR_API}/${fuarId}/firma-belge-kurallari/${fileTypeId}`,
    { params: { uyruk }, headers: getAuthHeader() }
  );
  return response.data;
}

// firmaProfileService.js'teki aynı isimli sabitin kopyası — uploadedBy/reviewedBy session'dan
// gelmiyor (bkz. o dosyanın başlık yorumu), gerçek bir user_id şart. Aynı test kullanıcısı.
const TEST_UPLOADED_BY_USER_ID = 21360;

/**
 * Fuara özel, genişleyebilir bir firma belgesini yükler — POST /firma/{firmaId}/documents/upload
 * (firmaProfileService.uploadFirmaGrupBelgesi'nin kullandığı GENEL uç, iki belge grubuyla sınırlı
 * değil; fileTypeId serbestçe geçirilebiliyor). fuarId BİLEREK gönderiliyor — bu belgeler
 * firma-geneli değil, bu fuara özel (backend fuar_id IS NULL OR = :fuarId ile karşılıyor, yani
 * firma zaten şirket-geneli bir kopyaya sahipse o da sayılır).
 */
export async function uploadRequiredFirmaBelge(firmaId, fileTypeId, fuarId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(
    `${FIRMA_API}/${firmaId}/documents/upload`,
    formData,
    {
      params: { fileTypeId, fuarId, uploadedBy: TEST_UPLOADED_BY_USER_ID },
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    }
  );
  return response.data;
}

/**
 * cardAccessMode -> { color, label, hint }, unknown/null'da nötr bir varsayılan döner.
 * #622: `yabanci` (Yabancı firma, `taxNumberGerekli === false`) → geçici erişimin ipucu vergi numarasından söz etmez (Yabancı firmadan istenmez, #434).
 */
export function cardAccessModeMeta(mode, { yabanci = false } = {}) {
  const meta = CARD_ACCESS_MODE[mode] || { color: 'secondary', label: i18n.t("services:cardAccessService.bilinmiyor"), hint: '' };
  if (yabanci && mode === 'TEMPORARY_24H') {
    return { color: meta.color, label: meta.label, hint: i18n.t("services:cardAccessService.gecici24SaatYabanciIpucu") };
  }
  return meta;
}
